<template>
  <div
    v-if="modelValue"
    class="sa-create-user-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="sa-create-user-title"
    @click.self="close"
  >
    <div class="sa-create-user-modal" @click.stop>
      <div class="sa-create-user-modal__accent" aria-hidden="true" />

      <header class="sa-create-user-modal__header">
        <div class="sa-create-user-modal__header-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
          </svg>
        </div>
        <div class="sa-create-user-modal__header-text">
          <h3 id="sa-create-user-title">{{ t('superAdmin.createUserTitle') }}</h3>
          <p>{{ t('superAdmin.createUserSubtitle') }}</p>
        </div>
        <button type="button" class="sa-create-user-modal__close" :aria-label="t('common.close')" @click="close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </header>

      <form class="sa-create-user-modal__body" @submit.prevent="submit">
        <div class="sa-create-user-modal__scroll">
        <section class="sa-form-section">
          <div class="sa-form-section__head">
            <span class="sa-form-section__badge">01</span>
            <div>
              <h4>{{ t('superAdmin.createUserSectionIdentity') }}</h4>
            </div>
          </div>
          <div class="sa-form-section__content">
            <div class="sa-field">
              <label for="sa-cu-nome">{{ t('auth.register.fullName') }}</label>
              <div class="sa-field__control">
                <span class="sa-field__icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                </span>
                <input
                  id="sa-cu-nome"
                  v-model="form.nomeCompleto"
                  type="text"
                  class="sa-field__input"
                  required
                  :disabled="saving"
                  autocomplete="name"
                />
              </div>
            </div>
            <div class="sa-field">
              <label for="sa-cu-email">{{ t('auth.register.email') }}</label>
              <div class="sa-field__control">
                <span class="sa-field__icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                </span>
                <input
                  id="sa-cu-email"
                  v-model="form.email"
                  type="email"
                  class="sa-field__input"
                  required
                  :disabled="saving"
                  autocomplete="email"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="sa-form-section sa-form-section--credentials">
          <div class="sa-form-section__head">
            <span class="sa-form-section__badge sa-form-section__badge--credentials">02</span>
            <div>
              <h4>{{ t('superAdmin.createUserSectionCredentials') }}</h4>
              <p class="sa-form-section__subtitle">{{ t('superAdmin.createUserSectionCredentialsHint') }}</p>
            </div>
          </div>
          <div class="sa-form-section__content sa-form-section__content--credentials">
            <div class="sa-credentials-banner">
              <div class="sa-credentials-banner__icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <p>{{ t('superAdmin.createUserCredentialsBanner') }}</p>
            </div>

            <div class="sa-credentials-fields">
              <div class="sa-field sa-field--password">
                <label for="sa-cu-senha">{{ t('auth.register.password') }}</label>
                <div class="sa-field__control sa-field__control--password">
                  <span class="sa-field__icon" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                  </span>
                  <input
                    id="sa-cu-senha"
                    v-model="form.senha"
                    :type="showPassword ? 'text' : 'password'"
                    class="sa-field__input"
                    required
                    minlength="6"
                    :disabled="saving"
                    autocomplete="new-password"
                    :placeholder="t('password.newPlaceholder')"
                  />
                  <button
                    type="button"
                    class="sa-field__toggle"
                    :aria-label="showPassword ? t('superAdmin.createUserHidePassword') : t('superAdmin.createUserShowPassword')"
                    :disabled="saving"
                    @click="showPassword = !showPassword"
                  >
                    <svg v-if="showPassword" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858 5.858a3 3 0 104.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                    <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  </button>
                </div>

                <div v-if="form.senha.length" class="sa-password-strength">
                  <div class="sa-password-strength__bar" role="progressbar" :aria-valuenow="passwordStrength.segments" aria-valuemin="0" aria-valuemax="3">
                    <span
                      v-for="n in 3"
                      :key="n"
                      class="sa-password-strength__seg"
                      :class="strengthSegClass(n)"
                    />
                  </div>
                  <span class="sa-password-strength__label" :class="`sa-password-strength__label--${passwordStrength.level}`">
                    {{ passwordStrengthLabel }}
                  </span>
                </div>

                <ul v-if="form.senha.length" class="sa-password-rules" :aria-label="t('password.rulesAria')">
                  <li :class="{ 'sa-password-rules__item--ok': passwordRules.min6 }">
                    <span class="sa-password-rules__icon">{{ passwordRules.min6 ? '✓' : '○' }}</span>
                    {{ t('password.ruleMin6') }}
                  </li>
                  <li :class="{ 'sa-password-rules__item--ok': passwordRules.hasUpper }">
                    <span class="sa-password-rules__icon">{{ passwordRules.hasUpper ? '✓' : '○' }}</span>
                    {{ t('password.ruleUpper') }}
                  </li>
                  <li :class="{ 'sa-password-rules__item--ok': passwordRules.hasDigit }">
                    <span class="sa-password-rules__icon">{{ passwordRules.hasDigit ? '✓' : '○' }}</span>
                    {{ t('password.ruleDigit') }}
                  </li>
                  <li :class="{ 'sa-password-rules__item--ok': passwordRules.hasSpecial }">
                    <span class="sa-password-rules__icon">{{ passwordRules.hasSpecial ? '✓' : '○' }}</span>
                    {{ t('password.ruleSpecial') }}
                  </li>
                </ul>
              </div>

              <div class="sa-field sa-field--password">
                <label for="sa-cu-confirm">{{ t('auth.register.confirmPassword') }}</label>
                <div
                  class="sa-field__control sa-field__control--password"
                  :class="{
                    'sa-field__control--error': confirmTouched && form.confirmSenha && !passwordsMatch,
                    'sa-field__control--success': confirmTouched && form.confirmSenha && passwordsMatch
                  }"
                >
                  <span class="sa-field__icon" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                  </span>
                  <input
                    id="sa-cu-confirm"
                    v-model="form.confirmSenha"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="sa-field__input"
                    required
                    :disabled="saving"
                    autocomplete="new-password"
                    :placeholder="t('password.repeatPlaceholder')"
                    @blur="confirmTouched = true"
                  />
                  <button
                    type="button"
                    class="sa-field__toggle"
                    :aria-label="showConfirmPassword ? t('superAdmin.createUserHidePassword') : t('superAdmin.createUserShowPassword')"
                    :disabled="saving"
                    @click="showConfirmPassword = !showConfirmPassword"
                  >
                    <svg v-if="showConfirmPassword" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858 5.858a3 3 0 104.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                    <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  </button>
                </div>

                <div
                  v-if="confirmTouched && form.confirmSenha"
                  class="sa-password-match"
                  :class="passwordsMatch ? 'sa-password-match--ok' : 'sa-password-match--error'"
                >
                  <svg v-if="passwordsMatch" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
                  <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  <span>{{ passwordsMatch ? t('superAdmin.createUserPasswordMatch') : t('superAdmin.createUserPasswordMismatch') }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="sa-form-section">
          <div class="sa-form-section__head">
            <span class="sa-form-section__badge">03</span>
            <div>
              <h4>{{ t('superAdmin.createUserSectionAccess') }}</h4>
            </div>
          </div>
          <div class="sa-form-section__content">
            <div class="sa-field">
              <span class="sa-field__label-row">{{ t('superAdmin.colRole') }}</span>
              <div class="sa-role-card" role="status" :aria-label="t('superAdmin.createUserRoleUserTitle')">
                <div class="sa-role-card__icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                </div>
                <div class="sa-role-card__body">
                  <div class="sa-role-card__title-row">
                    <strong class="sa-role-card__title">{{ t('superAdmin.createUserRoleUserTitle') }}</strong>
                    <span class="sa-role-card__badge">USER</span>
                  </div>
                  <p class="sa-role-card__desc">{{ t('superAdmin.createUserRoleUserDesc') }}</p>
                  <span class="sa-role-card__hint">{{ t('superAdmin.createUserRoleFixedHint') }}</span>
                </div>
                <div class="sa-role-card__check" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
              </div>
            </div>

            <div class="sa-plan-banner">
              <div class="sa-plan-banner__icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>
              </div>
              <div class="sa-plan-banner__text">
                <span class="sa-plan-banner__label">{{ t('superAdmin.currentPlan') }}</span>
                <strong class="sa-plan-banner__name">Pro</strong>
              </div>
              <span class="sa-plan-banner__pill">SaaS</span>
            </div>

            <div class="sa-form-row">
              <div class="sa-field">
                <label for="sa-cu-max-ws">{{ t('superAdmin.colMaxWorkspaces') }}</label>
                <div class="sa-field__control">
                  <span class="sa-field__icon" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                  </span>
                  <input
                    id="sa-cu-max-ws"
                    v-model.number="form.maxWorkspaces"
                    type="number"
                    min="0"
                    class="sa-field__input"
                    :placeholder="t('superAdmin.maxWorkspacesPlaceholder')"
                    :disabled="saving"
                  />
                </div>
              </div>
              <div class="sa-field">
                <label for="sa-cu-due">{{ t('superAdmin.billingDueDay') }}</label>
                <div class="sa-field__control">
                  <span class="sa-field__icon" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  </span>
                  <input
                    id="sa-cu-due"
                    v-model.number="form.diaVencimento"
                    type="number"
                    min="1"
                    max="31"
                    class="sa-field__input"
                    :placeholder="t('superAdmin.billingDueDayPlaceholder')"
                    :disabled="saving"
                  />
                </div>
              </div>
            </div>

            <div class="sa-field sa-field--valor-mensal">
              <label for="sa-cu-valor-mensal">{{ t('superAdmin.billing.valor') }}</label>
              <div class="sa-valor-mask-wrap">
                <span class="sa-valor-mask-prefix">R$</span>
                <input
                  id="sa-cu-valor-mensal"
                  :value="valorMensalDisplay"
                  type="text"
                  inputmode="decimal"
                  class="sa-valor-mask-input"
                  :placeholder="t('superAdmin.billing.valorPlaceholder')"
                  :disabled="saving"
                  @input="onValorMensalInput"
                />
              </div>
            </div>

            <p class="sa-form-hint">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              {{ t('superAdmin.billingDueDayHint') }}
            </p>

            <div class="sa-permission-card" :class="{ 'sa-permission-card--active': form.podeCriarWorkspace }">
              <div class="sa-permission-card__header">
                <button
                  type="button"
                  role="switch"
                  class="sa-access-switch__btn"
                  :aria-checked="form.podeCriarWorkspace"
                  :aria-label="t('superAdmin.createUserPodeCriarWorkspace')"
                  :disabled="saving"
                  @click="form.podeCriarWorkspace = !form.podeCriarWorkspace"
                >
                  <span class="sa-access-switch__track" aria-hidden="true">
                    <span class="sa-access-switch__thumb" />
                  </span>
                </button>
                <strong class="sa-permission-card__title">{{ t('superAdmin.createUserPodeCriarWorkspace') }}</strong>
                <span
                  class="sa-permission-card__status"
                  :class="form.podeCriarWorkspace ? 'sa-permission-card__status--on' : 'sa-permission-card__status--off'"
                >
                  {{ form.podeCriarWorkspace ? t('superAdmin.podeCriarWorkspaceActive') : t('superAdmin.podeCriarWorkspaceInactive') }}
                </span>
              </div>
              <p class="sa-permission-card__hint">{{ t('superAdmin.createUserPodeCriarWorkspaceHint') }}</p>
            </div>
          </div>
        </section>
        </div>

        <footer class="sa-create-user-modal__footer">
          <button type="button" class="sa-btn sa-btn--ghost" :disabled="saving" @click="close">
            {{ t('common.cancel') }}
          </button>
          <button type="submit" class="sa-btn sa-btn--primary" :disabled="saving || !isValid">
            <svg v-if="saving" class="sa-btn__spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25"/>
              <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
            </svg>
            <span v-if="saving">{{ t('superAdmin.createUserSaving') }}</span>
            <span v-else>{{ t('superAdmin.createUserSubmit') }}</span>
          </button>
        </footer>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/services/api'
import { evaluatePasswordStrength, passwordRulesStatus } from '@/composables/usePasswordStrength'
import { parseCurrencyMask, formatCurrencyMask } from '@/utils/currencyMask'

const props = defineProps({
  modelValue: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'created'])

const { t, locale } = useI18n()
const toast = useToast()

const saving = ref(false)
const confirmTouched = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const valorMensalDisplay = ref('')

const form = reactive({
  nomeCompleto: '',
  email: '',
  senha: '',
  confirmSenha: '',
  roleNome: 'USER',
  maxWorkspaces: 1,
  diaVencimento: null,
  valorMensal: null,
  podeCriarWorkspace: false
})

function onValorMensalInput (event) {
  const parsed = parseCurrencyMask(event?.target?.value ?? '')
  form.valorMensal = parsed > 0 ? parsed : null
  valorMensalDisplay.value = parsed > 0 ? formatCurrencyMask(parsed, locale.value) : ''
}

const passwordsMatch = computed(() => form.senha === form.confirmSenha)

const passwordStrength = computed(() => evaluatePasswordStrength(form.senha))
const passwordRules = computed(() => passwordRulesStatus(form.senha))
const passwordStrengthLabel = computed(() =>
  passwordStrength.value.labelKey ? t(passwordStrength.value.labelKey) : ''
)

const strengthSegClass = (n) => {
  const { level, segments } = passwordStrength.value
  if (n > segments) return 'sa-password-strength__seg--empty'
  if (level === 'weak' || level === 'empty') return 'sa-password-strength__seg--weak'
  if (level === 'medium') return 'sa-password-strength__seg--medium'
  return 'sa-password-strength__seg--strong'
}

const isValid = computed(() => {
  return (
    form.nomeCompleto?.trim() &&
    form.email?.trim() &&
    form.senha?.length >= 6 &&
    passwordsMatch.value
  )
})

const resetForm = () => {
  form.nomeCompleto = ''
  form.email = ''
  form.senha = ''
  form.confirmSenha = ''
  form.roleNome = 'USER'
  form.maxWorkspaces = 1
  form.diaVencimento = null
  form.valorMensal = null
  valorMensalDisplay.value = ''
  form.podeCriarWorkspace = false
  confirmTouched.value = false
  showPassword.value = false
  showConfirmPassword.value = false
}

const close = () => {
  emit('update:modelValue', false)
}

watch(() => props.modelValue, (open) => {
  if (open) resetForm()
})

const submit = async () => {
  if (!isValid.value) return
  saving.value = true
  try {
    const payload = {
      nomeCompleto: form.nomeCompleto.trim(),
      email: form.email.trim(),
      senha: form.senha,
      roleNome: form.roleNome
    }
    if (form.maxWorkspaces != null && form.maxWorkspaces !== '' && Number.isFinite(Number(form.maxWorkspaces))) {
      payload.maxWorkspaces = Number(form.maxWorkspaces)
    }
    if (form.diaVencimento != null && form.diaVencimento !== '' && Number.isFinite(Number(form.diaVencimento))) {
      payload.diaVencimento = Number(form.diaVencimento)
    }
    if (form.valorMensal != null && form.valorMensal > 0) {
      payload.valorMensal = form.valorMensal
    }
    payload.podeCriarWorkspace = Boolean(form.podeCriarWorkspace)
    await api.post('/super-admin/usuarios', payload)
    toast.success(t('superAdmin.createUserSuccess'))
    emit('created')
    close()
  } catch (err) {
    toast.error(err.response?.data?.error || t('superAdmin.createUserError'))
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.sa-create-user-overlay {
  position: fixed;
  inset: 0;
  z-index: 12000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: color-mix(in srgb, #0f172a 52%, transparent);
  backdrop-filter: blur(4px);
  animation: sa-overlay-in 0.2s ease;
}

.sa-create-user-modal {
  position: relative;
  width: min(560px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, #6366f1 22%, var(--border-primary));
  background: var(--bg-modal, var(--bg-primary));
  box-shadow:
    0 0 0 1px color-mix(in srgb, #fff 4%, transparent),
    0 28px 56px color-mix(in srgb, #000 28%, transparent);
  animation: sa-modal-in 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.sa-create-user-modal__accent {
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  background: linear-gradient(90deg, #0ea5e9, #6366f1, #8b5cf6);
  pointer-events: none;
}

.sa-create-user-modal__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--border-primary);
  background:
    radial-gradient(ellipse 80% 120% at 100% 0%, color-mix(in srgb, #6366f1 10%, transparent), transparent 55%),
    var(--bg-modal, var(--bg-primary));
}

.sa-create-user-modal__header-icon {
  width: 46px;
  height: 46px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  box-shadow: 0 6px 18px rgba(14, 165, 233, 0.35);
}

.sa-create-user-modal__header-icon svg {
  width: 24px;
  height: 24px;
}

.sa-create-user-modal__header-text {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.sa-create-user-modal__header-text h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-primary);
}

.sa-create-user-modal__header-text p {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--text-secondary);
}

.sa-create-user-modal__close {
  padding: 8px;
  border: none;
  border-radius: 999px;
  color: var(--text-secondary);
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.sa-create-user-modal__close:hover {
  background: color-mix(in srgb, var(--text-primary) 8%, transparent);
  color: var(--text-primary);
}

.sa-create-user-modal__body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.sa-create-user-modal__scroll {
  overflow-y: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  padding: 20px 24px;
  max-height: calc(100vh - 48px - 96px - 76px);
}

.sa-create-user-modal__scroll .sa-form-section:last-of-type {
  margin-bottom: 0;
}

.sa-form-section {
  margin-bottom: 20px;
}

.sa-form-section:last-of-type {
  margin-bottom: 0;
}

.sa-form-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.sa-form-section__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #6366f1;
  background: color-mix(in srgb, #6366f1 12%, transparent);
  border: 1px solid color-mix(in srgb, #6366f1 22%, transparent);
}

.sa-form-section__head h4 {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-secondary);
}

.sa-form-section__subtitle {
  margin: 3px 0 0;
  font-size: 0.75rem;
  line-height: 1.4;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: var(--text-tertiary);
}

.sa-form-section__badge--credentials {
  color: #059669;
  background: color-mix(in srgb, #10b981 12%, transparent);
  border-color: color-mix(in srgb, #10b981 24%, transparent);
}

.sa-form-section__content--credentials {
  padding: 0;
  overflow: hidden;
  border-color: color-mix(in srgb, #10b981 22%, var(--border-primary));
  background:
    linear-gradient(180deg, color-mix(in srgb, #10b981 4%, transparent), transparent 40%),
    color-mix(in srgb, var(--bg-secondary) 70%, transparent);
}

.sa-credentials-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid color-mix(in srgb, #10b981 16%, var(--border-primary));
  background: color-mix(in srgb, #10b981 6%, transparent);
}

.sa-credentials-banner__icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #059669;
  background: color-mix(in srgb, #10b981 16%, transparent);
  border: 1px solid color-mix(in srgb, #10b981 24%, transparent);
}

.sa-credentials-banner__icon svg {
  width: 18px;
  height: 18px;
}

.sa-credentials-banner p {
  margin: 0;
  padding-top: 2px;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--text-secondary);
}

.sa-credentials-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.sa-field--password {
  margin-bottom: 0;
}

.sa-field__control--password {
  padding-right: 4px;
}

.sa-field__control--success {
  border-color: color-mix(in srgb, #22c55e 50%, var(--border-primary));
  box-shadow: 0 0 0 3px color-mix(in srgb, #22c55e 12%, transparent);
}

.sa-field__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 2px;
  border: none;
  border-radius: 8px;
  color: var(--text-tertiary);
  background: transparent;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}

.sa-field__toggle:hover:not(:disabled) {
  color: var(--text-primary);
  background: color-mix(in srgb, var(--text-primary) 6%, transparent);
}

.sa-field__toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sa-field__toggle svg {
  width: 18px;
  height: 18px;
}

.sa-password-strength {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.sa-password-strength__bar {
  flex: 1;
  display: flex;
  gap: 5px;
}

.sa-password-strength__seg {
  flex: 1;
  height: 5px;
  border-radius: 999px;
  transition: background 0.3s ease;
}

.sa-password-strength__seg--empty {
  background: var(--border-primary);
  opacity: 0.45;
}

.sa-password-strength__seg--weak {
  background: linear-gradient(90deg, #f87171, #ef4444);
}

.sa-password-strength__seg--medium {
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
}

.sa-password-strength__seg--strong {
  background: linear-gradient(90deg, #4ade80, #22c55e);
}

.sa-password-strength__label {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sa-password-strength__label--weak {
  color: #dc2626;
}

.sa-password-strength__label--medium {
  color: #d97706;
}

.sa-password-strength__label--strong {
  color: #15803d;
}

.sa-password-strength__label--empty {
  color: var(--text-tertiary);
}

.sa-password-rules {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
  list-style: none;
  margin: 10px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: color-mix(in srgb, var(--bg-primary) 85%, transparent);
}

.sa-password-rules li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.6875rem;
  color: var(--text-tertiary);
}

.sa-password-rules__icon {
  width: 14px;
  text-align: center;
  font-size: 0.625rem;
}

.sa-password-rules__item--ok {
  color: #15803d;
  font-weight: 600;
}

.sa-password-match {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.sa-password-match svg {
  width: 14px;
  height: 14px;
}

.sa-password-match--ok {
  color: #15803d;
  background: color-mix(in srgb, #22c55e 12%, transparent);
  border: 1px solid color-mix(in srgb, #22c55e 24%, transparent);
}

.sa-password-match--error {
  color: #dc2626;
  background: color-mix(in srgb, #ef4444 10%, transparent);
  border: 1px solid color-mix(in srgb, #ef4444 22%, transparent);
}

.sa-form-section__content {
  padding: 16px;
  border-radius: 10px;
  border: 1px solid var(--border-primary);
  background: color-mix(in srgb, var(--bg-secondary) 70%, transparent);
}

.sa-field {
  margin-bottom: 14px;
}

.sa-field:last-child {
  margin-bottom: 0;
}

.sa-field label,
.sa-field__label-row {
  display: block;
  margin-bottom: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.sa-field__control {
  display: flex;
  align-items: center;
  gap: 0;
  border-radius: 9px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.sa-field__control:focus-within {
  border-color: color-mix(in srgb, #6366f1 55%, var(--border-primary));
  box-shadow: 0 0 0 3px color-mix(in srgb, #6366f1 14%, transparent);
}

.sa-field__control--error {
  border-color: color-mix(in srgb, #ef4444 55%, var(--border-primary));
  box-shadow: 0 0 0 3px color-mix(in srgb, #ef4444 10%, transparent);
}

.sa-field__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  flex-shrink: 0;
  color: var(--text-tertiary);
}

.sa-field__icon svg {
  width: 18px;
  height: 18px;
}

.sa-field__input {
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 0 12px 0 0;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
}

.sa-field__input::placeholder {
  color: var(--text-tertiary);
}

.sa-field__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sa-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.sa-form-row .sa-field {
  margin-bottom: 0;
}

.sa-field--valor-mensal {
  margin-top: 12px;
  margin-bottom: 0;
}

.sa-valor-mask-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 12px;
  border-radius: 9px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.sa-valor-mask-wrap:focus-within {
  border-color: color-mix(in srgb, #6366f1 55%, var(--border-primary));
  box-shadow: 0 0 0 3px color-mix(in srgb, #6366f1 14%, transparent);
}

.sa-valor-mask-prefix {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.sa-valor-mask-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
  outline: none;
}

.sa-valor-mask-input::placeholder {
  color: var(--text-tertiary);
  font-weight: 500;
}

.sa-valor-mask-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sa-field-feedback {
  margin: 8px 0 0;
  font-size: 0.75rem;
  font-weight: 500;
}

.sa-field-feedback--ok {
  color: #15803d;
}

.sa-field-feedback--error {
  color: #dc2626;
}

.sa-role-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, #0ea5e9 32%, var(--border-primary));
  background:
    linear-gradient(135deg, color-mix(in srgb, #0ea5e9 7%, transparent), color-mix(in srgb, #6366f1 5%, transparent)),
    var(--bg-primary);
  box-shadow: 0 4px 16px color-mix(in srgb, #0ea5e9 8%, transparent);
}

.sa-role-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.sa-role-card__icon svg {
  width: 20px;
  height: 20px;
}

.sa-role-card__body {
  flex: 1;
  min-width: 0;
}

.sa-role-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.sa-role-card__title {
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--text-primary);
}

.sa-role-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #0369a1;
  background: color-mix(in srgb, #0ea5e9 14%, transparent);
  border: 1px solid color-mix(in srgb, #0ea5e9 28%, transparent);
}

.sa-role-card__desc {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--text-secondary);
}

.sa-role-card__hint {
  display: inline-flex;
  margin-top: 8px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-tertiary);
  background: color-mix(in srgb, var(--text-primary) 5%, transparent);
}

.sa-role-card__check {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #15803d;
  background: color-mix(in srgb, #22c55e 14%, transparent);
  border: 1px solid color-mix(in srgb, #22c55e 28%, transparent);
}

.sa-role-card__check svg {
  width: 14px;
  height: 14px;
}

.sa-plan-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, #6366f1 28%, var(--border-primary));
  background:
    linear-gradient(135deg, color-mix(in srgb, #6366f1 8%, transparent), color-mix(in srgb, #8b5cf6 6%, transparent)),
    var(--bg-primary);
}

.sa-plan-banner__icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
  background: color-mix(in srgb, #6366f1 14%, transparent);
}

.sa-plan-banner__icon svg {
  width: 18px;
  height: 18px;
}

.sa-plan-banner__text {
  flex: 1;
  min-width: 0;
}

.sa-plan-banner__label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
}

.sa-plan-banner__name {
  display: block;
  margin-top: 2px;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.sa-plan-banner__pill {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 999px;
  color: #6366f1;
  background: color-mix(in srgb, #6366f1 12%, transparent);
  border: 1px solid color-mix(in srgb, #6366f1 22%, transparent);
}

.sa-form-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 12px 0 14px;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--text-tertiary);
}

.sa-form-hint svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  margin-top: 1px;
}

.sa-permission-card {
  padding: 14px;
  border-radius: 10px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.sa-permission-card--active {
  border-color: color-mix(in srgb, #22c55e 28%, var(--border-primary));
  background: color-mix(in srgb, #22c55e 4%, var(--bg-primary));
}

.sa-permission-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  user-select: none;
}

.sa-access-switch__btn {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.sa-access-switch__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.sa-access-switch__btn:focus-visible .sa-access-switch__track {
  outline: 2px solid color-mix(in srgb, #6366f1 55%, transparent);
  outline-offset: 2px;
}

.sa-permission-card__title {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}

.sa-permission-card__hint {
  margin: 10px 0 0 56px;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--text-tertiary);
}

.sa-permission-card__status {
  flex-shrink: 0;
  min-width: 76px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: center;
  white-space: nowrap;
}

.sa-permission-card__status--on {
  color: #15803d;
  background: color-mix(in srgb, #22c55e 14%, transparent);
  border: 1px solid color-mix(in srgb, #22c55e 26%, transparent);
}

.sa-permission-card__status--off {
  color: #dc2626;
  background: color-mix(in srgb, #ef4444 12%, transparent);
  border: 1px solid color-mix(in srgb, #ef4444 24%, transparent);
}

.sa-access-switch__track {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background: color-mix(in srgb, #ef4444 18%, var(--bg-secondary));
  border: 1px solid color-mix(in srgb, #ef4444 35%, transparent);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.sa-permission-card--active .sa-access-switch__track {
  background: color-mix(in srgb, #22c55e 35%, var(--border-primary));
  border-color: color-mix(in srgb, #22c55e 40%, transparent);
}

.sa-access-switch__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px color-mix(in srgb, #000 18%, transparent);
  transition: transform 0.2s ease;
}

.sa-permission-card--active .sa-access-switch__thumb {
  transform: translateX(20px);
}

.sa-create-user-modal__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-top: 0;
  padding: 16px 24px 24px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-modal, var(--bg-primary));
}

.sa-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 18px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.sa-btn svg {
  width: 18px;
  height: 18px;
}

.sa-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.sa-btn--ghost {
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid var(--border-primary);
}

.sa-btn--ghost:hover:not(:disabled) {
  background: color-mix(in srgb, var(--text-primary) 6%, transparent);
  color: var(--text-primary);
}

.sa-btn--primary {
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.35);
}

.sa-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(99, 102, 241, 0.42);
}

.sa-btn__spinner {
  animation: sa-spin 0.8s linear infinite;
}

@keyframes sa-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes sa-modal-in {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes sa-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 560px) {
  .sa-form-row {
    grid-template-columns: 1fr;
  }

  .sa-role-card {
    flex-wrap: wrap;
  }

  .sa-password-rules {
    grid-template-columns: 1fr;
  }

  .sa-permission-card__hint {
    margin-left: 0;
    padding-left: 56px;
  }

  .sa-create-user-modal__footer {
    flex-direction: column-reverse;
  }

  .sa-btn {
    width: 100%;
  }
}
</style>
